import React from 'react'
import {Box, InputBase, MenuItem, Select, SelectChangeEvent, Stack, SxProps, Theme, Typography} from '@mui/material'
import {CONTROL_HEIGHT, NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {Option} from '../../patient/tabs/therapy/therapyOptions'

// Reusable {value, unitType} pair input — new-style equivalent of the legacy
// src/components/templates/TherapyDialogs/UnitSelector.tsx (antd InputNumber + Select
// grouped into a single pill via addonAfter). Rendered here as one bordered control
// (not two separate boxes) so the number field and the unit select always share the
// same height/border and read as one field, matching the legacy grouped appearance.

export interface UnitValue {
  value?: number
  unitType?: string
}

interface UnitValueFieldProps {
  label?: string
  value: UnitValue
  onChange: (value: UnitValue) => void
  unitOptions: Option[]
  disabled?: boolean
  required?: boolean
  sx?: SxProps<Theme>
}

export default function UnitValueField({
  label,
  value,
  onChange,
  unitOptions,
  disabled,
  required,
  sx,
}: UnitValueFieldProps): JSX.Element {
  const numberValue = value?.value !== undefined && value?.value !== null ? String(value.value) : ''

  const handleNumberChange = (raw: string): void => {
    const parsed = raw === '' ? undefined : Number(raw)
    onChange({...value, value: Number.isNaN(parsed) ? undefined : parsed})
  }

  const handleUnitChange = (unitType: string): void => {
    onChange({...value, unitType: unitType || undefined})
  }

  return (
    <Stack spacing={0.5} sx={sx}>
      {label && (
        <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
          {label}{required && ' *'}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          height: CONTROL_HEIGHT,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: NEUTRAL_SCALE[200],
          backgroundColor: disabled ? NEUTRAL_SCALE[100] : '#fff',
          overflow: 'hidden',
          opacity: disabled ? 0.6 : 1,
          '&:focus-within': {borderColor: 'primary.main'},
        }}
      >
        <InputBase
          value={numberValue}
          onChange={(e) => handleNumberChange(e.target.value)}
          placeholder="Numero"
          disabled={disabled}
          sx={{
            flex: 1,
            height: '100%',
            px: '14px',
            borderRight: '1px solid',
            borderColor: NEUTRAL_SCALE[200],
            '& input': {height: '100%', padding: 0, boxSizing: 'border-box'},
          }}
        />
        <Select
          value={value?.unitType ?? ''}
          onChange={(e: SelectChangeEvent) => handleUnitChange(e.target.value)}
          disabled={disabled}
          displayEmpty
          variant="standard"
          disableUnderline
          sx={{
            width: 130,
            flexShrink: 0,
            height: '100%',
            backgroundColor: NEUTRAL_SCALE[50],
            '& .MuiSelect-select': {
              height: '100% !important',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
              px: '12px',
            },
            '& .MuiSelect-icon': {color: NEUTRAL_SCALE[500]},
          }}
        >
          <MenuItem disabled value="">
            <Box component="em" sx={{color: NEUTRAL_SCALE[500], fontStyle: 'normal'}}>Unità</Box>
          </MenuItem>
          {unitOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </Box>
    </Stack>
  )
}
